import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import { MAX_FLAG_TITLE_LENGTH } from './constants';
import type { FormData } from './types';

export const getTruncatedTitleForFlag = (title: string) => {
	if (title.trim().length <= MAX_FLAG_TITLE_LENGTH) {
		return title;
	}

	return `${title.trim().slice(0, MAX_FLAG_TITLE_LENGTH)}...`;
};

type FormattedData = {
	finalData: FormData;
	wasLinkPrefixedWithExpectedProtocol: boolean;
	protocolPrefix: string | typeof undefined;
};

export const formatFormData = (formData: FormData): FormattedData => {
	const { link } = formData;
	const finalData: FormData = formData;
	const expectedProtocolPrefixes = ['https://', 'http://', 'ftp://', 'tel:', 'mailto:'];
	let wasLinkPrefixedWithExpectedProtocol = false;

	// regex to match URL protocol e.g. https://
	const protoRegex = new RegExp('^(.*:(//)?)(.*)$', 'gm');

	const match = protoRegex.exec(link);
	const protocolPrefix = match ? match[1] : undefined;

	// If the link is prefixed with an expected protocol, do not add any prefix
	// else prefix it with https://
	for (let i = 0; i < expectedProtocolPrefixes.length; i += 1) {
		if (link.indexOf(expectedProtocolPrefixes[i]) === 0) {
			wasLinkPrefixedWithExpectedProtocol = true;

			return { finalData, wasLinkPrefixedWithExpectedProtocol, protocolPrefix };
		}
	}
	// prefix with https:// protocol
	finalData.link = `https://${link}`;

	return { finalData, wasLinkPrefixedWithExpectedProtocol, protocolPrefix };
};

interface Options {
	leading?: boolean;
	maxWait?: number;
	trailing?: boolean;
}

const defer = <TReturnValue extends {}>() => {
	let resolve: (value: TReturnValue | PromiseLike<TReturnValue>) => void = noop;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	let reject: (reason?: any) => void = noop;

	const promise = new Promise<TReturnValue>((rslv, rjct) => {
		resolve = rslv;
		reject = rjct;
	});

	return {
		promise,
		resolve,
		reject,
	};
};

export const batch = <TReturnValue extends {}, TArg>(
	func: (arg: TArg[]) => Promise<TReturnValue>,
	wait: number,
	options: Options = {},
) => {
	let arrArg: TArg[] = [];
	let deferred = defer<TReturnValue>();

	const callThenResetArgs = (args: TArg[]) => {
		try {
			const result = func(args);
			deferred.resolve(result);
		} catch (err) {
			deferred.reject(err);
		}

		arrArg = [];
		deferred = defer<TReturnValue>();
	};

	const debouncedFunc = debounce(callThenResetArgs, wait, options);

	return (arg: TArg): Promise<TReturnValue> => {
		arrArg = arrArg.concat(arg);
		debouncedFunc(arrArg);
		return deferred.promise;
	};
};
